import { useCallback, useEffect, useRef, useState } from "react";
import { SearchFocusTarget, User } from "@/App";
import { useApp } from "@/contexts/AppContext";
import { Header } from "./Header";
import { Footer } from "./Footer";
import { AccountSidebar } from "./account/AccountSidebar";
import type {
  AccountActivity,
  AccountEditMode,
  AccountFormData,
  AccountStatistics,
} from "./account/types";
import { AccountSettingsPanel } from "./account/settings/AccountSettingsPanel";
import ProfilePictureUploader, {
  type ProfilePictureUploaderHandle,
} from "./account/settings/ProfilePictureUploader";
import { buildProfilePictureSrc } from "@/utils/profilePicture";
import { API_URL } from "@/utils/axios";
import { useAuth } from "@/contexts/AuthContext";
import { userAccountService } from "@/services/userAccount.service";
import { accountService } from "@/services/account.service";
import { getMlSmartSearchEnabled } from "@/services/publicSystemSettings.service";
import type { ActivityTimePeriod, WebsiteActionType } from "@/types/activity.types";
import {
  arePasswordRequirementsMet,
  getPasswordRequirements,
} from "@/utils/passwordValidation";
// @ts-ignore
import { toast } from "sonner";

const EMAIL_CHANGE_INFO_KEY = "emailChangeInfo";
const HOME_AI_SEARCH_STORAGE_KEY = "home.aiSearchEnabled";

const readHomeAiSearchPreference = (): boolean => {
  if (typeof window === "undefined") return true;
  try {
    return window.localStorage.getItem(HOME_AI_SEARCH_STORAGE_KEY) !== "false";
  } catch {
    return true;
  }
};

const persistHomeAiSearchPreference = (enabled: boolean) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(HOME_AI_SEARCH_STORAGE_KEY, String(enabled));
  } catch {
    // Ignore localStorage write errors.
  }
};

interface AccountOverviewProps {
  navigate: (page: string) => void;
  currentUser: User | null;
  onLogout: () => void;
  onSearch: (query: string) => void;
  onNavigateToSearchPage: (target: SearchFocusTarget) => void;
}

/**
 * Account overview screen combining sidebar and settings cards, plus the profile picture editor.
 */
export function AccountOverview({
  navigate,
  currentUser,
  onLogout,
  onSearch,
  onNavigateToSearchPage,
}: AccountOverviewProps) {
  const { t, language } = useApp();
  const {
    user: authUser,
    updateProfilePicture,
    updateUsername,
    updateEmail,
    updateHomeAiSearchEnabled,
    logout,
    isLoading,
  } = useAuth();
  const resolvedLanguage = language || "de";
  const scrollToTop = useCallback(() => {
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, []);
  useEffect(() => {
    scrollToTop();
  }, [scrollToTop]);
  const baseProfileUrl = `${API_URL}/users/me/profile-picture`;
  const resolvedProfileUrl = currentUser?.profilePicture || "";
  const [editMode, setEditMode] = useState<AccountEditMode>("none");
  const [profilePicture, setProfilePicture] =
    useState<string>(resolvedProfileUrl);
  const [profilePicturePreview, setProfilePicturePreview] = useState<
    string | null
  >(null);
  const uploaderRef = useRef<ProfilePictureUploaderHandle | null>(null);
  const uploaderSectionRef = useRef<HTMLDivElement | null>(null);
  const isMountedRef = useRef(true);
  const [isMobile, setIsMobile] = useState(false);
  const [isEditingPicture, setIsEditingPicture] = useState(false);
  const [formData, setFormData] = useState<AccountFormData>({
    newEmail: currentUser?.email || "",
    newUsername: currentUser?.username || "",
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const passwordRequirements = getPasswordRequirements(formData.newPassword);
  const isPasswordValid = arePasswordRequirementsMet(passwordRequirements);
  const passwordsMatch =
    formData.newPassword === formData.confirmPassword &&
    formData.confirmPassword !== "";
  const isPasswordSaveDisabled =
    !formData.currentPassword || !isPasswordValid || !passwordsMatch;
  const normalizedEmail = formData.newEmail.trim();
  const isEmailFormatValid =
    normalizedEmail.length > 0 &&
    normalizedEmail.length <= 255 &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail);

  const resetPasswordFields = useCallback(() => {
    setFormData((prev) => ({
      ...prev,
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    }));
  }, [setFormData]);

  const resetEmailField = useCallback(() => {
    setFormData((prev) => ({
      ...prev,
      newEmail: currentUser?.email || "",
    }));
  }, [currentUser?.email, setFormData]);

  useEffect(() => {
    // Keep the form fields aligned with stored user data after updates.
    setFormData((prev) => ({
      ...prev,
      newEmail: currentUser?.email || "",
      newUsername: currentUser?.username || "",
    }));
  }, [currentUser?.email, currentUser?.username]);

  useEffect(() => {
    // Keep the current user picture URL or blank when none is available.
    setProfilePicture(resolvedProfileUrl);
  }, [currentUser, resolvedProfileUrl]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const media = window.matchMedia("(max-width: 1023px)");
    const handleChange = () => setIsMobile(media.matches);
    handleChange();
    media.addEventListener("change", handleChange);
    return () => media.removeEventListener("change", handleChange);
  }, []);

  const [recentActivity, setRecentActivity] = useState<AccountActivity[]>([]);
  const [statistics, setStatistics] = useState<AccountStatistics | null>(null);
  const [activitySearch, setActivitySearch] = useState("");
  const [activityActionType, setActivityActionType] = useState<
    WebsiteActionType | "ALL"
  >("ALL");
  const [activityTimePeriod, setActivityTimePeriod] =
    useState<ActivityTimePeriod>("MONTH");
  const [activityPageSize, setActivityPageSize] = useState(10);
  const [activityPage, setActivityPage] = useState(1);
  const [activityTotalPages, setActivityTotalPages] = useState(0);
  const [activityTotalItems, setActivityTotalItems] = useState(0);
  const [isDataExporting, setIsDataExporting] = useState(false);
  const [isAccountDeleting, setIsAccountDeleting] = useState(false);
  const [mlSmartSearchEnabled, setMlSmartSearchEnabled] = useState(true);
  const [homeAiSearchEnabled, setHomeAiSearchEnabled] = useState<boolean>(
    () => readHomeAiSearchPreference()
  );
  const [homeAiSearchSaving, setHomeAiSearchSaving] = useState(false);

  useEffect(() => {
    if (!currentUser) return;
    let isMounted = true;
    const loadAccountOverview = async () => {
      try {
        const statisticsData = await accountService.getStatistics();
        if (!isMounted) return;
        setStatistics(statisticsData);
      } catch (error: any) {
        toast.error(error?.response?.data?.message ?? t("common.genericError"));
      }
    };
    loadAccountOverview();
    return () => {
      isMounted = false;
    };
  }, [currentUser, t]);

  useEffect(() => {
    if (activityPage !== 1) {
      setActivityPage(1);
    }
  }, [activitySearch, activityActionType, activityTimePeriod, activityPageSize]);

  useEffect(() => {
    if (!currentUser) return;
    let isMounted = true;
    const loadActivity = async () => {
      try {
        const trimmedSearch = activitySearch.trim();
        const actionFilter =
          activityActionType === "ALL" ? undefined : activityActionType;
        const timePeriodFilter =
          activityTimePeriod === "ALL" ? undefined : activityTimePeriod;
        const response = await accountService.getActivity({
          page: activityPage - 1,
          size: activityPageSize,
          search: trimmedSearch || undefined,
          actionType: actionFilter,
          timePeriod: timePeriodFilter,
        });
        if (!isMounted) return;
        if (response.totalPages > 0 && activityPage > response.totalPages) {
          setActivityPage(response.totalPages);
          return;
        }
        setRecentActivity(response.items);
        setActivityTotalPages(response.totalPages);
        setActivityTotalItems(response.totalElements);
      } catch (error: any) {
        toast.error(error?.response?.data?.message ?? t("common.genericError"));
      }
    };
    loadActivity();
    return () => {
      isMounted = false;
    };
  }, [
    currentUser,
    activityPage,
    activityPageSize,
    activitySearch,
    activityActionType,
    activityTimePeriod,
    t,
  ]);

  useEffect(() => {
    let cancelled = false;
    const loadSetting = async () => {
      const enabled = await getMlSmartSearchEnabled();
      if (cancelled) return;
      setMlSmartSearchEnabled(enabled);
    };
    void loadSetting();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!authUser?.id) {
      setHomeAiSearchEnabled(readHomeAiSearchPreference());
      return;
    }

    let cancelled = false;
    const localPreference = readHomeAiSearchPreference();

    const syncPreference = async () => {
      if (typeof authUser.homeAiSearchEnabled === "boolean") {
        setHomeAiSearchEnabled(authUser.homeAiSearchEnabled);
        persistHomeAiSearchPreference(authUser.homeAiSearchEnabled);
        return;
      }

      try {
        const response = await userAccountService.getHomeAiSearchPreference();
        if (cancelled) return;
        updateHomeAiSearchEnabled(response.enabled);
        if (typeof response.enabled === "boolean") {
          setHomeAiSearchEnabled(response.enabled);
          persistHomeAiSearchPreference(response.enabled);
          return;
        }

        const backfill = await userAccountService.updateHomeAiSearchPreference({
          enabled: localPreference,
        });
        if (cancelled) return;
        updateHomeAiSearchEnabled(backfill.enabled);
        if (typeof backfill.enabled === "boolean") {
          setHomeAiSearchEnabled(backfill.enabled);
          persistHomeAiSearchPreference(backfill.enabled);
        } else {
          setHomeAiSearchEnabled(localPreference);
        }
      } catch {
        setHomeAiSearchEnabled(localPreference);
      }
    };

    void syncPreference();
    return () => {
      cancelled = true;
    };
  }, [authUser?.homeAiSearchEnabled, authUser?.id, updateHomeAiSearchEnabled]);

  useEffect(() => {
    if (editMode !== "password") {
      resetPasswordFields();
    }
  }, [editMode, resetPasswordFields]);

  useEffect(() => {
    if (editMode !== "email") {
      resetEmailField();
    }
  }, [editMode, resetEmailField]);

  const handleProfilePictureChange = useCallback(() => {
    uploaderRef.current?.openFileDialog();
  }, []);

  const handleProfilePictureRemove = useCallback(() => {
    void uploaderRef.current?.removeImage();
  }, []);

  const handleProfileImageError = () => {
    setProfilePicture("");
  };

  const handleProfileUploadSuccess = () => {
    // Cache-bust the image after upload so the browser fetches the fresh avatar.
    const refreshedUrl =
      buildProfilePictureSrc(baseProfileUrl, true, Date.now()) ?? "";
    if (isMountedRef.current) {
      setProfilePicture(refreshedUrl);
    }
    updateProfilePicture(refreshedUrl);
  };

  const handleProfileRemoveSuccess = () => {
    if (isMountedRef.current) {
      setProfilePicture("");
    }
    updateProfilePicture(null);
  };

  useEffect(() => {
    if (!isLoading && !currentUser) {
      navigate("login");
    }
  }, [isLoading, currentUser, navigate]);


  if (isLoading) {
    return null;
  }

  if (!currentUser) {
    return null;
  }

  /**
   * Persists the email change request and updates local auth state.
   */
  const handleSaveEmail = async () => {
    const currentEmail = (currentUser?.email ?? "").trim();

    if (!normalizedEmail) {
      toast.error(t("account.emailRequired"));
      return;
    }

    if (normalizedEmail.length > 255) {
      toast.error(t("account.emailTooLong"));
      return;
    }

    if (!isEmailFormatValid) {
      toast.error(t("account.emailInvalid"));
      return;
    }

    if (normalizedEmail.toLowerCase() === currentEmail.toLowerCase()) {
      toast.error(t("account.emailUnchanged"));
      return;
    }
    try {
      const response = await userAccountService.changeEmail({
        email: normalizedEmail,
      });
      updateEmail(response.email);
      if (response.verificationRequired) {
        toast.success(t("auth.verificationEmailSent"));
        toast.info(t("auth.verifyBeforeLogin"));
      } else {
        toast.info(t("auth.verificationNotRequired"));
        toast.info(t("auth.verificationNotRequiredHint"));
      }
      if (typeof window !== "undefined") {
        try {
          sessionStorage.setItem(
            EMAIL_CHANGE_INFO_KEY,
            JSON.stringify({
              email: response.email,
              verificationRequired: response.verificationRequired,
            })
          );
        } catch {
          // ignore storage failures
        }
      }
      navigate("email-change-info");
      setEditMode("none");
      logout();
    } catch (error: any) {
      const messageKey = error?.response?.data?.message;
      toast.error(messageKey ? t(messageKey) : t("common.genericError"));
      resetEmailField();
    }
  };

  /**
   * Persists the username change request and updates local auth state.
   */
  const handleSaveUsername = async () => {
    const normalizedUsername = formData.newUsername.trim();
    const currentUsername = (currentUser?.username ?? "").trim();

    if (!normalizedUsername) {
      toast.error(t("account.usernameRequired"));
      return;
    }

    if (normalizedUsername.length > 255) {
      toast.error(t("account.usernameTooLong"));
      return;
    }

    if (normalizedUsername.toLowerCase() === currentUsername.toLowerCase()) {
      toast.error(t("account.usernameUnchanged"));
      return;
    }

    try {
      const response = await userAccountService.changeUsername({
        username: normalizedUsername,
      });
      updateUsername(response.username);
      setFormData((prev) => ({ ...prev, newUsername: response.username }));
      toast.success(t("common.saved"));
      setEditMode("none");
    } catch (error: any) {
      const messageKey = error?.response?.data?.message;
      toast.error(messageKey ? t(messageKey) : t("common.genericError"));
    }
  };

  /**
   * Persists the password change request and invalidates the active session.
   */
  const handleSavePassword = async () => {
    if (isPasswordSaveDisabled) {
      return;
    }
    try {
      await userAccountService.changePassword({
        currentPassword: formData.currentPassword,
        newPassword: formData.newPassword,
      });
      toast.success(t("common.saved"));
      resetPasswordFields();
      setEditMode("none");
      logout();
    } catch (error: any) {
      const messageKey = error?.response?.data?.message;
      toast.error(messageKey ? t(messageKey) : t("common.genericError"));
      resetPasswordFields();
    }
  };

  const handleDownloadMyData = async () => {
    if (isDataExporting) return;
    setIsDataExporting(true);
    try {
      const payload = await userAccountService.exportMyData();
      const serialized = JSON.stringify(payload, null, 2);
      const blob = new Blob([serialized], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const now = new Date();
      const yyyy = String(now.getFullYear());
      const mm = String(now.getMonth() + 1).padStart(2, "0");
      const dd = String(now.getDate()).padStart(2, "0");
      const fileName = `webkurator-data-export-${yyyy}-${mm}-${dd}.json`;
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success(t("account.dataExportSuccess"));
    } catch (error: any) {
      const messageKey = error?.response?.data?.message;
      toast.error(messageKey ? t(messageKey) : t("account.dataExportFailed"));
    } finally {
      setIsDataExporting(false);
    }
  };

  const handleDeleteMyAccount = async (currentPassword: string) => {
    if (isAccountDeleting) return;
    setIsAccountDeleting(true);
    try {
      await userAccountService.deleteMyAccount({ currentPassword });
      toast.success(t("account.accountDeleteSuccess"));
      onLogout();
    } catch (error: any) {
      const messageKey = error?.response?.data?.message;
      toast.error(messageKey ? t(messageKey) : t("account.accountDeleteFailed"));
      throw error;
    } finally {
      setIsAccountDeleting(false);
    }
  };

  const handleHomeAiSearchToggle = async (enabled: boolean) => {
    const previous = homeAiSearchEnabled;
    setHomeAiSearchEnabled(enabled);
    persistHomeAiSearchPreference(enabled);
    updateHomeAiSearchEnabled(enabled);

    if (!authUser?.id) {
      return;
    }

    setHomeAiSearchSaving(true);
    try {
      const response = await userAccountService.updateHomeAiSearchPreference({
        enabled,
      });
      if (typeof response.enabled === "boolean") {
        setHomeAiSearchEnabled(response.enabled);
        persistHomeAiSearchPreference(response.enabled);
      }
      updateHomeAiSearchEnabled(response.enabled);
      toast.success(t("common.saved"));
    } catch (error: any) {
      setHomeAiSearchEnabled(previous);
      persistHomeAiSearchPreference(previous);
      updateHomeAiSearchEnabled(previous);
      const messageKey = error?.response?.data?.message;
      toast.error(messageKey ? t(messageKey) : t("common.genericError"));
    } finally {
      setHomeAiSearchSaving(false);
    }
  };

  return (
    <div
      className="flex flex-col min-h-screen"
      style={{ backgroundColor: "var(--background)" }}
    >
      <Header
        navigate={navigate}
        currentUser={currentUser}
        onLogout={onLogout}
        currentPage="account"
        samePageHandlers={{ account: scrollToTop }}
        onNavigateToSearchPage={onNavigateToSearchPage}
      />

      <main className="flex-1 pt-4 pb-4">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="mb-6">
            <h2 className="text-primary-heading mb-1">{t("account.title")}</h2>
            <p className="text-secondary">{t("account.subtitle")}</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-1 space-y-6">
              <AccountSidebar
                currentUser={currentUser}
                statistics={statistics}
                profilePicture={profilePicture}
                profilePicturePreview={profilePicturePreview}
                language={resolvedLanguage}
                onProfilePictureError={handleProfileImageError}
                onProfilePictureChange={handleProfilePictureChange}
                onProfilePictureRemove={handleProfilePictureRemove}
                isEditingPicture={isEditingPicture}
                showProfilePictureHint={isEditingPicture && isMobile}
                onProfilePictureHintClick={() =>
                  uploaderSectionRef.current?.scrollIntoView({
                    behavior: "smooth",
                    block: "start",
                  })
                }
                t={t}
              />
            </div>
            <div className="lg:col-span-2 space-y-6">
              <div
                ref={uploaderSectionRef}
                className={
                  isEditingPicture
                    ? "rounded-lg border border-border p-4"
                    : "hidden"
                }
                style={
                  isEditingPicture
                    ? {
                        backgroundColor:
                          "color-mix(in srgb, var(--surface) 50%, var(--background))",
                      }
                    : undefined
                }
              >
                <ProfilePictureUploader
                  ref={uploaderRef}
                  imageUrl={profilePicture || undefined}
                  onUploadSuccess={handleProfileUploadSuccess}
                  onRemoveSuccess={handleProfileRemoveSuccess}
                  onEditingChange={setIsEditingPicture}
                  onPreviewChange={setProfilePicturePreview}
                  showActionButtons={false}
                  showPreview={false}
                  showHint={false}
                  autoSaveOnExit
                />
              </div>
              <AccountSettingsPanel
                navigate={navigate}
                editMode={editMode}
                setEditMode={setEditMode}
                currentEmail={currentUser.email}
                isEmailSaveDisabled={!isEmailFormatValid}
                formData={formData}
                setFormData={setFormData}
                onSaveEmail={handleSaveEmail}
                onSaveUsername={handleSaveUsername}
                onSavePassword={handleSavePassword}
                onDownloadMyData={handleDownloadMyData}
                isDataExporting={isDataExporting}
                onDeleteMyAccount={handleDeleteMyAccount}
                isAccountDeleting={isAccountDeleting}
                passwordRequirements={passwordRequirements}
                passwordsMatch={passwordsMatch}
                isPasswordSaveDisabled={isPasswordSaveDisabled}
                recentActivity={recentActivity}
                activitySearch={activitySearch}
                onActivitySearchChange={setActivitySearch}
                activityActionType={activityActionType}
                onActivityActionTypeChange={setActivityActionType}
                activityTimePeriod={activityTimePeriod}
                onActivityTimePeriodChange={setActivityTimePeriod}
                activityPageSize={activityPageSize}
                onActivityPageSizeChange={setActivityPageSize}
                activityPage={activityPage}
                activityTotalPages={activityTotalPages}
                activityTotalItems={activityTotalItems}
                onActivityPageChange={setActivityPage}
                homeAiSearchEnabled={homeAiSearchEnabled}
                homeAiSearchForcedOff={!mlSmartSearchEnabled}
                homeAiSearchSaving={homeAiSearchSaving}
                onHomeAiSearchToggle={handleHomeAiSearchToggle}
                isEmailVerified={currentUser.isVerified ?? false}
                t={t}
                language={resolvedLanguage}
              />
            </div>
          </div>
        </div>
      </main>

      <Footer navigate={navigate} />
    </div>
  );
}
